/**
 * DOIs in running text — a reference list pasted in any style — and the forms they
 * are written back in.
 *
 * The same DOI turns up written half a dozen ways: `doi:10.1001/…` (AMA, APA 6),
 * `doi: 10.1109/…` (IEEE), `DOI: 10.1037/…`, `DOI 10.1007/…`, `http://dx.doi.org/…`,
 * `https://doi.org/…` (APA 7 and Crossref's display guidelines), a bare `10.1037/…`,
 * percent-encoded (`%2F` for the slash), inside brackets, and followed by the full stop
 * that ends the reference. `findDois` reads all of them, keeps the offsets of exactly
 * what was written, and leaves the sentence's punctuation outside the span.
 *
 * Nothing here touches the network.
 */

/** The forms a DOI can be written back in. */
export type DoiStyle = "https" | "ama" | "ieee" | "bare";

export type DoiProblem =
  /** A `doi:` label or a doi.org link followed by something that is not a DOI. */
  | "not-a-doi"
  /** `10.1037/` with nothing after the slash. */
  | "no-suffix"
  /** A look-alike dash, an invisible character or another non-ASCII character inside. */
  | "unusual-character"
  /** Ends in a hyphen at the end of the line — probably split across two lines. */
  | "split-line";

export interface DoiSpan {
  /** Offset of the written form in the line, its label or link included. */
  start: number;
  /** Offset just past the written form; trailing punctuation stays outside. */
  end: number;
  /** Exactly what was written: "DOI: http://dx.doi.org/10.1037/a0016755". */
  written: string;
  /** The DOI itself, decoded and without a prefix — or null when there is none. */
  doi: string | null;
  problems: DoiProblem[];
  /** The DOI sits inside some other web address, such as a publisher's article link. */
  inLink: boolean;
}

/** doi.org, and the older resolver addresses that still turn up in reference lists. */
const RESOLVER = String.raw`(?:https?:\/\/)?(?:(?:dx\.|www\.)?doi\.org|doi\.acm\.org|doi\.wiley\.com)\/`;

/** Everywhere a DOI can start. Each branch runs to the next space; the end is trimmed later. */
const RE_DOI_START = new RegExp(
  [
    // A label, then the DOI or a link to it: "doi:10.…", "DOI: 10.…", "DOI 10.…".
    // Without a link, the token must hold a digit or a slash, so "DOI: not
    // available" is prose rather than a broken DOI.
    String.raw`\bdoi(?:\s*:\s*|\s+(?=10\.|${RESOLVER}))(?:${RESOLVER}|(?=\S*[\d/]))\S*`,
    // A resolver link on its own.
    String.raw`(?<![\w.%-])${RESOLVER}\S*`,
    // The URI form.
    String.raw`\binfo:doi\/\S*`,
    // A bare DOI: "10.", a registrant code of four to nine digits, a slash.
    String.raw`(?<![\w.%-])10\.\d{4,9}(?:\.\d+)*\/\S*`,
  ].join("|"),
  "gi"
);
const RE_PREFIX = new RegExp(
  String.raw`^(?:doi(?:\s*:\s*|\s+))?(?:${RESOLVER}|info:doi\/)?`,
  "i"
);
const RE_HAS_RESOLVER = new RegExp(RESOLVER, "i");
const RE_DOI_SHAPE = /^10\.\d{4,9}(?:\.\d+)*\/\S+$/;
const RE_PREFIX_ONLY = /^10\.\d{4,9}(?:\.\d+)*\/?$/;
const RE_PERCENT_ESCAPE = /%[0-9a-f]{2}/i;
const RE_QUERY = /[?#]/;
const RE_NON_ASCII = /[^\x21-\x7e]/;
const RE_NON_ASCII_ALL = /[^\x21-\x7e]/g;
/** A publisher link carries the DOI and then the view: …/10.1002/x/full, …/epdf. */
const RE_LINK_TAIL =
  /\/(?:full|abstract|abs|epdf|pdf|epub|fulltext(?:\.html)?|html)\/?$/i;
const RE_TOKEN_BEFORE = /\S*$/;
const RE_URL_LIKE = /[a-z][a-z\d+.-]*:\/\/|^\W*www\./i;
const RE_RETRIEVED_FROM = /\bRetrieved\s+from:?\s*$/i;
const RE_LINE_BREAK = /\r\n|\r|\n/;
const RE_ONLY_SPACE = /^\s*$/;
const RE_STOP_THEN_SPACE = /^\.\s*$/;
const RE_URL_UNSAFE = /[%"#?\s]/g;
const RE_HTML_SPECIAL = /[&<>"']/g;

/** Sentence punctuation and quotes that can follow a DOI but are never its last character. */
const TRAILING = new Set([
  ".",
  ",",
  ";",
  ":",
  "!",
  "?",
  "'",
  '"',
  "’",
  "”",
  "»",
]);
const OPENER_OF: Record<string, string> = {
  ")": "(",
  "]": "[",
  "}": "{",
  ">": "<",
};

function occurrences(text: string, char: string): number {
  return text.split(char).length - 1;
}

/**
 * Strip what follows a DOI but is not part of it. A closing bracket is kept only when
 * the DOI opened it — 10.1016/S0140-6736(97)11096-0 keeps its parenthesis, a DOI written
 * "(https://doi.org/10.1037/a0016755)" loses the one around it.
 */
function trimTrailing(body: string): string {
  let end = body.length;
  while (end > 0) {
    const char = body[end - 1];
    const head = body.slice(0, end);
    const opener = OPENER_OF[char];
    const unbalanced =
      opener !== undefined &&
      occurrences(head, char) > occurrences(head, opener);
    if (!(TRAILING.has(char) || unbalanced)) {
      break;
    }
    end -= 1;
  }
  return body.slice(0, end);
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function isInsideLink(line: string, start: number): boolean {
  const token = RE_TOKEN_BEFORE.exec(line.slice(0, start))?.[0] ?? "";
  return RE_URL_LIKE.test(token);
}

/** The body of a match as a DOI, with what is wrong with it. */
function judge(
  decoded: string,
  rest: string
): Pick<DoiSpan, "doi" | "problems"> {
  if (!RE_DOI_SHAPE.test(decoded)) {
    return {
      doi: null,
      problems: [RE_PREFIX_ONLY.test(decoded) ? "no-suffix" : "not-a-doi"],
    };
  }
  const problems: DoiProblem[] = [];
  if (RE_NON_ASCII.test(decoded)) {
    problems.push("unusual-character");
  }
  if (decoded.endsWith("-") && RE_ONLY_SPACE.test(rest)) {
    problems.push("split-line");
  }
  return { doi: decoded, problems };
}

function readSpan(line: string, start: number, raw: string): DoiSpan {
  const prefix = RE_PREFIX.exec(raw)?.[0] ?? "";
  const inLink = isInsideLink(line, start);
  let body = raw.slice(prefix.length);
  if (inLink || RE_HAS_RESOLVER.test(prefix)) {
    body = body.split(RE_QUERY)[0];
  }
  body = trimTrailing(body);
  if (inLink) {
    body = trimTrailing(body.replace(RE_LINK_TAIL, ""));
  }
  const written = `${prefix}${body}`.trimEnd();
  const end = start + written.length;
  const decoded = RE_PERCENT_ESCAPE.test(body) ? safeDecode(body) : body;
  return {
    start,
    end,
    written,
    inLink,
    ...judge(decoded, line.slice(end)),
  };
}

/** Every DOI-like span in one line, in order. */
export function findDois(line: string): DoiSpan[] {
  return [...line.matchAll(RE_DOI_START)].map((match) =>
    readSpan(line, match.index, match[0])
  );
}

/* --------------------------------------------------------------- writing */

/** The DOI as a link — characters that would end or break a URL are percent-encoded. */
export function doiUrl(doi: string): string {
  const path = doi.replace(
    RE_URL_UNSAFE,
    (char) =>
      `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`
  );
  return `https://doi.org/${path}`;
}

/** The address a live link points at: every unsafe character encoded, slashes kept. */
export function doiHref(doi: string): string {
  return `https://doi.org/${doi.split("/").map(encodeURIComponent).join("/")}`;
}

/** One DOI in the chosen form. */
export function writeDoi(doi: string, style: DoiStyle): string {
  switch (style) {
    case "https":
      return doiUrl(doi);
    case "ama":
      return `doi:${doi}`;
    case "ieee":
      return `doi: ${doi}`;
    default:
      return doi;
  }
}

/* ------------------------------------------------------------ extraction */

export interface FoundDoi {
  doi: string;
  /** 1-based line of the DOI's first appearance. */
  line: number;
  problems: DoiProblem[];
}

export interface MalformedDoi {
  written: string;
  line: number;
  problems: DoiProblem[];
}

export interface DoiScan {
  /** Distinct DOIs in order of first appearance; DOIs are case-insensitive. */
  dois: FoundDoi[];
  /** Spans that are labelled or linked as DOIs but are not one. */
  malformed: MalformedDoi[];
}

/** Every distinct DOI in a block of text, publisher links included. */
export function scanDois(text: string): DoiScan {
  const seen = new Set<string>();
  const dois: FoundDoi[] = [];
  const malformed: MalformedDoi[] = [];
  for (const [index, line] of text.split(RE_LINE_BREAK).entries()) {
    for (const span of findDois(line)) {
      if (span.doi === null) {
        const key = `${index}|${span.written}`;
        if (!seen.has(key)) {
          seen.add(key);
          malformed.push({
            written: span.written,
            line: index + 1,
            problems: span.problems,
          });
        }
        continue;
      }
      const key = span.doi.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        dois.push({ doi: span.doi, line: index + 1, problems: span.problems });
      }
    }
  }
  return { dois, malformed };
}

/* ---------------------------------------------------------- list rewrite */

/** What to do with a full stop after a DOI that ends its reference. */
export type FinalStop = "keep" | "remove" | "add";

export interface DoiListOptions {
  style: DoiStyle;
  /** Remove "Retrieved from" before a DOI — an APA 6 habit APA 7 dropped. */
  dropRetrieved: boolean;
  finalStop: FinalStop;
}

export interface DoiEdit {
  /** 1-based. */
  line: number;
  before: string;
  after: string;
}

export interface DoiFlag {
  line: number;
  written: string;
  /** The DOI, when there is one — a DOI inside a link, or one with a stray character. */
  doi: string | null;
  problems: DoiProblem[];
  /** A DOI inside a publisher's link, left as it is. */
  inLink: boolean;
  /** The non-ASCII characters inside the DOI, for "unusual-character". */
  characters: string[];
}

/** Where a DOI sits in the rewritten text — for highlighting and for live links. */
export interface DoiMark {
  start: number;
  end: number;
  /** The DOI a live link points at; "" when the span is not a usable DOI. */
  doi: string;
  state: "changed" | "unchanged" | "flagged";
}

export interface DoiListResult {
  text: string;
  marks: DoiMark[];
  edits: DoiEdit[];
  flags: DoiFlag[];
  /** DOIs rewritten or already in the chosen form. */
  count: number;
  /** Every DOI-like span found, the ones left alone included. */
  found: number;
}

interface LineResult extends Omit<DoiListResult, "text"> {
  text: string;
}

function flagFor(span: DoiSpan, line: number): DoiFlag {
  return {
    line,
    written: span.written,
    doi: span.doi,
    problems: span.problems,
    inLink: span.inLink,
    characters: [...new Set(span.doi?.match(RE_NON_ASCII_ALL) ?? [])],
  };
}

/** The full-stop change for a DOI that ends its line: what to consume, what to add. */
function stopChange(
  rest: string,
  mode: FinalStop
): { consumed: string; added: string } {
  if (mode === "remove" && RE_STOP_THEN_SPACE.test(rest)) {
    return { consumed: ".", added: "" };
  }
  if (mode === "add" && RE_ONLY_SPACE.test(rest)) {
    return { consumed: "", added: "." };
  }
  return { consumed: "", added: "" };
}

/** Split "…40. Retrieved from " into the text to keep and the phrase to drop. */
function splitRetrieved(
  before: string,
  drop: boolean
): { kept: string; removed: string } {
  const match = drop ? RE_RETRIEVED_FROM.exec(before) : null;
  if (!match) {
    return { kept: before, removed: "" };
  }
  return {
    kept: before.slice(0, match.index),
    removed: before.slice(match.index),
  };
}

/** A span the rewrite leaves alone: copied as written, marked and reported. */
function keepFlagged(result: LineResult, span: DoiSpan, number: number): void {
  result.marks.push({
    start: result.text.length,
    end: result.text.length + span.written.length,
    doi: "",
    state: "flagged",
  });
  result.text += span.written;
  result.flags.push(flagFor(span, number));
}

function markState(span: DoiSpan, changed: boolean): DoiMark["state"] {
  if (span.problems.length > 0) {
    return "flagged";
  }
  return changed ? "changed" : "unchanged";
}

function rewriteLine(
  line: string,
  number: number,
  options: DoiListOptions
): LineResult {
  const result: LineResult = {
    text: "",
    marks: [],
    edits: [],
    flags: [],
    count: 0,
    found: 0,
  };
  const spans = findDois(line);
  result.found = spans.length;
  let cursor = 0;
  for (const [index, span] of spans.entries()) {
    const doi = span.inLink ? null : span.doi;
    if (doi === null) {
      result.text += line.slice(cursor, span.start);
      keepFlagged(result, span, number);
      cursor = span.end;
      continue;
    }
    const { kept, removed } = splitRetrieved(
      line.slice(cursor, span.start),
      options.dropRetrieved
    );
    const stop =
      index === spans.length - 1
        ? stopChange(line.slice(span.end), options.finalStop)
        : { consumed: "", added: "" };
    const rewritten = writeDoi(doi, options.style);
    const before = `${removed}${span.written}${stop.consumed}`;
    const after = `${rewritten}${stop.added}`;
    result.text += kept;
    result.marks.push({
      start: result.text.length,
      end: result.text.length + rewritten.length,
      doi,
      state: markState(span, before !== after),
    });
    result.text += after;
    result.count += 1;
    if (before !== after) {
      result.edits.push({ line: number, before, after });
    }
    if (span.problems.length > 0) {
      result.flags.push(flagFor(span, number));
    }
    cursor = span.end + stop.consumed.length;
  }
  result.text += line.slice(cursor);
  return result;
}

/**
 * Rewrite every DOI in a reference list in one form. Malformed DOIs and DOIs inside
 * another link are left exactly as written and reported in `flags`.
 */
export function formatDoiList(
  text: string,
  options: DoiListOptions
): DoiListResult {
  const lines: string[] = [];
  const all: DoiListResult = {
    text: "",
    marks: [],
    edits: [],
    flags: [],
    count: 0,
    found: 0,
  };
  let offset = 0;
  for (const [index, line] of text.split(RE_LINE_BREAK).entries()) {
    const result = rewriteLine(line, index + 1, options);
    for (const mark of result.marks) {
      all.marks.push({
        ...mark,
        start: mark.start + offset,
        end: mark.end + offset,
      });
    }
    all.edits.push(...result.edits);
    all.flags.push(...result.flags);
    all.count += result.count;
    all.found += result.found;
    lines.push(result.text);
    offset += result.text.length + 1;
  }
  all.text = lines.join("\n");
  return all;
}

function escapeHtml(text: string): string {
  return text.replace(RE_HTML_SPECIAL, (char) => `&#${char.charCodeAt(0)};`);
}

/**
 * The rewritten list as HTML — a paragraph per reference, every usable DOI a live
 * link to doi.org — for pasting into Word or Google Docs.
 */
export function doiListHtml(result: DoiListResult): string {
  let html = "";
  let cursor = 0;
  for (const mark of result.marks) {
    html += escapeHtml(result.text.slice(cursor, mark.start));
    const visible = escapeHtml(result.text.slice(mark.start, mark.end));
    html += mark.doi
      ? `<a href="${escapeHtml(doiHref(mark.doi))}">${visible}</a>`
      : visible;
    cursor = mark.end;
  }
  html += escapeHtml(result.text.slice(cursor));
  return html
    .split("\n")
    .filter((line) => line.trim() !== "")
    .map((line) => `<p>${line}</p>`)
    .join("");
}
